/**
 * /register — create a NEW workspace, or attach an additional company
 * to an already-logged-in identity (ID.2 unified identity).
 *
 * Mirrors `frontend/src/app/(auth)/register/page.tsx`. The page detects
 * "add-a-company mode" via `?add=1` OR the presence of an active
 * session — in that case email/name fields are pre-filled and locked
 * (backend keys the attach on the session identity, not the posted
 * email), and the client-side password requirement is dropped.
 *
 * Post-submit branches:
 *   - normal totp_required → /2fa-verify
 *   - M-1 totp_enrollment_required → /2fa-setup?required=1 (rare on a
 *     fresh signup, but possible when ATTACHING a company makes the
 *     identity mandatory-2FA via an existing super_admin/hr_manager
 *     membership elsewhere)
 *   - 409 identity_exists → inline panel with a link to /login
 *   - otherwise → /dashboard
 *
 * Migration note: 1:1 port. next/link → <Link>, useRouter → useNavigate,
 * useSearchParams → Route.useSearch(). The `addCompanySchema` /
 * `registerSchema` switch and the FIELDS list are unchanged.
 */

import { useEffect, useState } from "react";
import {
  Link,
  createFileRoute,
  useNavigate,
} from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { authApi, toApiError, isTotpEnrollmentRequired } from "@/lib/api";
import { clearClientAuthState, useAuth } from "@/lib/auth";
import {
  registerSchema,
  addCompanySchema,
  type RegisterValues,
} from "@/lib/auth/schemas";
import { handleAuthError } from "@/lib/auth/form-errors";
import { secondFactorPending } from "@/lib/auth/passkey";
import { suggestHandle } from "@/lib/workspace-handle";
import { readAttribution, storeFirstTouch } from "@/lib/attribution";

/**
 * Visual required-field marker. Audit 4 / 28-FIELD-PLACEMENT-AUDIT
 * Quick-Win #9 (2026-06-03) — /register previously had ZERO required
 * markers despite 5 mandatory fields. The asterisk is aria-hidden
 * because the semantics are carried by `aria-required` on each
 * <Input> sibling (screen readers don't announce text-content
 * asterisks).
 */
function RequiredMarker() {
  return (
    <span aria-hidden className="ml-0.5 text-destructive">
      *
    </span>
  );
}

// 2026-06-02 UX fix: company_slug dropped from the register form.
// Backend now auto-derives it from company_name; users rename it
// later in Settings → Workspace. See backend/internal/handlers/
// auth.go slugifyCompanyName + deriveFreeSlug.
const FIELDS = [
  "company_name",
  "company_slug",
  "contact_phone",
  "first_name",
  "last_name",
  "email",
  "password",
] as const;

const registerSearchSchema = z.object({
  // `?add=1` — surfaced from the account menu's "Create or add a
  // company" link (Phase 3 will land that menu item). Coerced to a
  // number because TanStack Router's URL parser hands us strings; we
  // treat the literal 1 as truthy.
  add: z.coerce.number().optional().catch(undefined),
  // `?plan=posh_only` — "Start with POSH only" from the marketing POSH and
  // pricing pages (migration 162). Anything else is ignored (dropped, not
  // an error page).
  plan: z.enum(["posh_only"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_auth/register")({
  validateSearch: registerSearchSchema,
  component: RegisterPage,
});

function RegisterPage() {
  const navigate = useNavigate();
  const { add, plan } = Route.useSearch();
  const poshOnly = plan === "posh_only";
  const { user, refresh } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [identityExists, setIdentityExists] = useState<string | null>(
    null,
  );

  // Add-mode is set when the account-menu link includes ?add=1, OR
  // when there's already an active session. In either case the
  // backend will ATTACH the new company to THIS identity.
  const addMode = add === 1 || !!user;

  const form = useForm<RegisterValues>({
    resolver: zodResolver(addMode ? addCompanySchema : registerSchema),
    defaultValues: {
      company_name: "",
      company_slug: "",
      contact_phone: "",
      first_name: user?.first_name ?? "",
      last_name: user?.last_name ?? "",
      email: user?.email ?? "",
      password: "",
    },
  });

  const companyName = form.watch("company_name");

  // First-touch signup source (migration 145). The marketing site
  // normally wrote `wr_attr` already; a visitor who lands straight on
  // /register (e.g. from a campaign link) gets it recorded here. First
  // touch wins — an existing cookie is never overwritten.
  useEffect(() => {
    try {
      const marketing = import.meta.env.VITE_MARKETING_URL ?? "https://workright.in";
      storeFirstTouch(document, window.location, new URL(marketing).hostname, new Date());
    } catch {
      // cookies blocked / bad URL: attribution is best-effort
    }
  }, []);

  async function onSubmit(values: RegisterValues) {
    setSubmitting(true);
    setIdentityExists(null);
    try {
      const res = await authApi.register({
        company_name: values.company_name,
        // Optional (lifecycle walkthrough 2026-10): blank → the backend
        // derives it from company_name; a taken one is a 409 slug_taken.
        company_slug: values.company_slug || undefined,
        contact_phone: values.contact_phone || undefined,
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        // add-mode: the backend ignores this (the session identity
        // already has a credential).
        password: addMode ? "" : values.password,
        // Where this new customer first came from — only for a brand-new
        // signup (adding a company to an existing account is not one).
        attribution: addMode ? undefined : (readAttribution(document.cookie) ?? undefined),
        // POSH only signup: a 14-day POSH trial for the admin alone.
        plan: poshOnly ? "posh_only" : undefined,
      });
      // Auth boundary: register sets fresh tenant cookies WITHOUT passing
      // through login()/activate(), so clear the prior session's client
      // state here (query cache + stored active-entity id) — otherwise a
      // stale X-Entity-Id from a previous tenant 403s every call in the
      // new workspace, bricking it at the DPDP consent modal.
      clearClientAuthState();
      const me = await refresh();
      toast.success(
        addMode
          ? `${values.company_name} created and ready.`
          : "Workspace created — welcome to WorkRight.",
      );
      if (secondFactorPending(res)) {
        void navigate({
          to: "/2fa-verify",
          search: { next: "/" },
          replace: true,
        });
        return;
      }
      // Use the slug from the just-refreshed /me — the in-render
      // `user` state hasn't propagated yet from refresh()'s setState.
      const slug = me?.tenant?.slug ?? "";
      void navigate({
        to: "/app/$tenantSlug",
        params: { tenantSlug: slug },
        replace: true,
      });
    } catch (err) {
      if (isTotpEnrollmentRequired(err)) {
        void navigate({
          to: "/2fa-setup",
          search: { required: 1, next: "/" },
          replace: true,
        });
        return;
      }
      const apiErr = toApiError(err);
      if (apiErr.error === "identity_exists") {
        setIdentityExists(apiErr.message);
        return;
      }
      handleAuthError(err, form.setError, FIELDS);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl">
          {poshOnly
            ? "Start with POSH Only"
            : addMode
              ? "Add a Company"
              : "Create Your Workspace"}
        </CardTitle>
        <CardDescription>
          {poshOnly
            ? "14 days free for you to try POSH. Add your team when you pay — priced by company size."
            : addMode
              ? "Spin up another company on your account. You'll be its admin and can switch to it from the account menu any time."
              : "You'll be the company admin. You can invite your team next."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {identityExists && (
          <div
            role="alert"
            className="mb-4 space-y-2 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm"
          >
            <p className="font-medium text-amber-900">{identityExists}</p>
            <p className="text-amber-800">
              This email already has an account. Sign in, then add this
              company from your account menu.
            </p>
            <Button asChild size="sm" className="mt-1">
              <Link to="/login">Go to Sign In</Link>
            </Button>
          </div>
        )}
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="company_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Company Name
                    <RequiredMarker />
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Acme Corp"
                      data-testid="register-company-name"
                      aria-required
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Optional workspace handle (lifecycle walkthrough
                2026-10). Blank → derived from the company name; it can
                be renamed later in Settings → Workspace. */}
            <FormField
              control={form.control}
              name="company_slug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Workspace Web Address (Optional)</FormLabel>
                  {/* FormControl wraps the Input itself so the label's `for`
                      and aria-describedby land on the form element, not the
                      wrapper div; the /app/ prefix is purely visual. */}
                  <div className="flex items-center gap-1">
                    <span className="shrink-0 text-sm text-muted-foreground">/app/</span>
                    <FormControl>
                      <Input
                        placeholder={suggestHandle(companyName) || "your-company"}
                        autoCapitalize="none"
                        autoComplete="off"
                        spellCheck={false}
                        data-testid="register-company-slug"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                  </div>
                  <FormDescription>
                    Leave blank and we&apos;ll make one from your company name.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="first_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      First Name
                      <RequiredMarker />
                    </FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="given-name"
                        placeholder="Asha"
                        readOnly={addMode}
                        aria-readonly={addMode || undefined}
                        aria-required
                        data-testid="register-firstname"
                        className={
                          addMode
                            ? "bg-muted/40 text-muted-foreground"
                            : undefined
                        }
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="last_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Last Name
                      <RequiredMarker />
                    </FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="family-name"
                        placeholder="Verma"
                        readOnly={addMode}
                        aria-readonly={addMode || undefined}
                        aria-required
                        data-testid="register-lastname"
                        className={
                          addMode
                            ? "bg-muted/40 text-muted-foreground"
                            : undefined
                        }
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Work Email
                    <RequiredMarker />
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="email"
                      placeholder="you@acme.com"
                      readOnly={addMode}
                      aria-readonly={addMode || undefined}
                      aria-required
                      data-testid="register-email"
                      className={
                        addMode
                          ? "bg-muted/40 text-muted-foreground"
                          : undefined
                      }
                      {...field}
                    />
                  </FormControl>
                  {addMode && (
                    <FormDescription>
                      The new company is added to your existing account.
                    </FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            {!addMode && (
              <FormField
                control={form.control}
                name="contact_phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Phone{" "}
                      <span className="text-muted-foreground">
                        (Optional)
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="tel"
                        autoComplete="tel"
                        placeholder="+91 98765 43210"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            {!addMode && (
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Password
                      <RequiredMarker />
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        autoComplete="new-password"
                        placeholder="••••••••••••"
                        data-testid="register-password"
                        aria-required
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      At least 12 characters, mixing 3 of: lowercase,
                      uppercase, digit, symbol.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={submitting}
              data-testid="register-submit"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {addMode ? "Create Company" : "Create Workspace"}
            </Button>
          </form>
        </Form>
        {!addMode && (
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link
              to="/login"
              className="inline-flex items-center font-medium text-primary hover:underline [@media(pointer:coarse)]:min-h-11"
            >
              Sign in
            </Link>
          </p>
        )}
      </CardContent>
    </Card>
  );
}
